import axios from "axios";
import { DispDataEntry, DispVSLevelEntry, TankTempChartData } from "../../interfaces/interfaces";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export async function getTransactionData(date: string) {
    const params = new URLSearchParams();
    params.append("date", date);

    try {
        const res = await axios.get(`${API_BASE_URL}/transactions`, {
            params
        });
        
        return res.data;

    } catch (err: any) {
        return "Failed to fetch dataset";
    }
}


export async function getDispensedPresetData(site: string, date: string) {
    // Get the dispensed Preset Transaction data for a site and start date
    const params = new URLSearchParams();
    params.append("site", site);
    params.append("date", date);

    try {
        const res = await axios.get(`${API_BASE_URL}/dispensed-in-preset-range`, {
            params
        });
        
        return res.data;

    } catch (err: any) {
        return "Failed to fetch dataset";
    }
}

export async function getDispensedAndLevelData(site: string, date: string) {
    // Get the dispensed Preset Transaction data for a site and start date
    const params = new URLSearchParams();
    params.append("site", site);
    params.append("date", date);

    try {
        const res = await axios.get(`${API_BASE_URL}/dispense-and-level`, {
            params
        });
        
        return res.data;

    } catch (err: any) {
        return "Failed to fetch dataset";
    }
}


export async function runCheckTransIds(transData: DispDataEntry[]) {
    // Run peak valley analysis and return results

    const body = {"data": transData}

    try {
        const res = await axios.post(`${API_BASE_URL}/trans-id-check`, body);
        
        return res.data;

    } catch (err: any) {
        return "Failed to run analysis";
    }
}


export async function runCheckGrossTotals(transData: DispDataEntry[]) {
    // Run peak valley analysis and return results

    const body = {"data": transData}

    try {
        const res = await axios.post(`${API_BASE_URL}/trans-gross-total-check`, body);
        
        return res.data;

    } catch (err: any) {
        return "Failed to run analysis";
    }
}


export async function runDispVSLevels(transData: DispDataEntry[]) {
    // Run Dispenses VS Tank Level Calculation

    const body = {"data": transData}

    try {
        const res = await axios.post(`${API_BASE_URL}/trans-vs-level-check`, body);
        
        return res.data;

    } catch (err: any) {
        return "Failed to run analysis";
    }
}


export const parseTankData = (raw: any): any[] => {
  try {
    if (!raw || typeof raw.data !== "string") return [];

    // Step 1: Replace NaN with null (JSON-safe)
    const cleanedString = raw.data.replace(/NaN/g, "null");

    // Step 2: Parse the cleaned string
    const parsed = JSON.parse(cleanedString);

    // Step 3: Optional sanitization (if needed)
    return parsed.map((entry: any) => ({
      ...entry,
      total_dispensed: entry.total_dispensed ?? null,
      physical_total_tank_level: entry.physical_total_tank_level ?? null,
      calculated_level_of_tank_after_dispense: entry.calculated_level_of_tank_after_dispense ?? null,
    }));
  } catch (err) {
    console.error("Failed to parse tank data:", err);
    return [];
  }
};


export const prepareChartData = (rawData: DispVSLevelEntry[]): any[] => {
  return rawData.map(entry => ({
    time_bucket: entry.time_bucket,
    physical_total_tank_level: entry.physical_total_tank_level ?? 0,
    calculated_level_of_tank_after_dispense: entry.calculated_level_of_tank_after_dispense ?? 0,
  }));
};


export function prepareTemperatureChartData(rawData: TankTempChartData[]): {
  data: any[];
  yKeys: string[];
} {
  const tankTempMap: Record<string, Record<string, number>> = {};

  rawData.forEach((entry) => {
    const { timestamp, source_name, avg_temp } = entry;

    if (typeof avg_temp === "number") {
      if (!tankTempMap[timestamp]) {
        tankTempMap[timestamp] = {};
      }

      tankTempMap[timestamp][source_name] = avg_temp;
    }
  });

  const data = Object.entries(tankTempMap).map(([timestamp, temps]) => ({
    timestamp,
    ...temps,
  }));

  const yKeys = Array.from(new Set(rawData.map((d) => d.source_name)));

  return { data, yKeys };
}

export function prepareTankLevelChartData(rawData: TankTempChartData[]): {
  data: any[];
  yKeys: string[];
} {
  const tankLevelMap: Record<string, Record<string, number>> = {};

  rawData.forEach((entry) => {
    const { timestamp, source_name, value } = entry;

    if (typeof value === "number") {
      if (!tankLevelMap[timestamp]) {
        tankLevelMap[timestamp] = {};
      }

      tankLevelMap[timestamp][source_name] = value;
    }
  });

  const sortedData = Object.entries(tankLevelMap)
    .map(([timestamp, values]) => ({
      timestamp,
      ...values
    }))
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const yKeys = Array.from(
    new Set(rawData.map((entry) => entry.source_name))
  );

  return {
    data: sortedData,
    yKeys
  };
}